import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DahshboardOverview,
  RevenueDailyTrend,
  AdminEarnings,
  UserMonthlyStats,
  AstrologerMonthlyStats,
} from "../services/dashboard.services";
import { useNavigate } from "react-router-dom";

const COLORS = ["#0d6efd", "#17a2b8", "#28a745", "#6c757d"];

const formatDailyTrend = (trendRes) => {
  const raw = trendRes?.data;
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((item) => ({
      label: new Date(item.date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      }),
      date: item.date,
      totalAmount: parseFloat(item.totalAmount) || 0,
    }));
  }

  if (raw.labels && Array.isArray(raw.labels)) {
    return raw.labels.map((label, index) => ({
      label,
      consultation_revenue: raw.consultations?.revenue?.[index] ?? 0,
      consultation_count: raw.consultations?.count?.[index] ?? 0,
      remedies_revenue: raw.remedies?.revenue?.[index] ?? 0,
      remedies_count: raw.remedies?.count?.[index] ?? 0,
      puja_revenue: raw.puja?.revenue?.[index] ?? 0,
      puja_count: raw.puja?.count?.[index] ?? 0,
    }));
  }

  return [];
};

const isSimpleTrend = (trend) =>
  trend.length > 0 && trend[0].totalAmount !== undefined;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 2 }, (_, i) => CURRENT_YEAR - i);

const Dashboard = () => {
  const [data, setData] = useState({});
  const [earnings, setEarnings] = useState({});
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);
  const [userStatsYear, setUserStatsYear] = useState(CURRENT_YEAR);
  const [userMonthlyStats, setUserMonthlyStats] = useState([]);
  const [yearlyTotalUsers, setYearlyTotalUsers] = useState(0);
  const [userStatsLoading, setUserStatsLoading] = useState(true);
  const [astroStatsYear, setAstroStatsYear] = useState(CURRENT_YEAR);
  const [astroMonthlyStats, setAstroMonthlyStats] = useState([]);
  const [yearlyTotalAstrologers, setYearlyTotalAstrologers] = useState(0);
  const [highestAstroMonth, setHighestAstroMonth] = useState("");
  const [highestAstroMonthCount, setHighestAstroMonthCount] = useState(0);
  const [astroStatsLoading, setAstroStatsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, earningsRes, trendRes] = await Promise.all([
        DahshboardOverview(period),
        AdminEarnings(),
        RevenueDailyTrend(period),
      ]);

      if (overviewRes?.data) setData(overviewRes.data);
      if (earningsRes?.data) setEarnings(earningsRes.data);

      setTrend(formatDailyTrend(trendRes));
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setTrend([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    const fetchUserStats = async () => {
      setUserStatsLoading(true);
      try {
        const userRes = await UserMonthlyStats(userStatsYear);
        setUserMonthlyStats(Array.isArray(userRes?.data) ? userRes.data : []);
        setYearlyTotalUsers(userRes?.yearly_total_users ?? 0);
      } catch (err) {
        console.error("User monthly stats fetch error:", err);
        setUserMonthlyStats([]);
        setYearlyTotalUsers(0);
      } finally {
        setUserStatsLoading(false);
      }
    };

    fetchUserStats();
  }, [userStatsYear]);

  useEffect(() => {
    const fetchAstroStats = async () => {
      setAstroStatsLoading(true);
      try {
        const astroRes = await AstrologerMonthlyStats(astroStatsYear);
        setAstroMonthlyStats(
          Array.isArray(astroRes?.data) ? astroRes.data : [],
        );
        setYearlyTotalAstrologers(astroRes?.yearly_total_astrologers ?? 0);
        setHighestAstroMonth(astroRes?.highest_month ?? "");
        setHighestAstroMonthCount(astroRes?.highest_month_astrologers ?? 0);
      } catch (err) {
        console.error("Astrologer monthly stats fetch error:", err);
        setAstroMonthlyStats([]);
        setYearlyTotalAstrologers(0);
        setHighestAstroMonth("");
        setHighestAstroMonthCount(0);
      } finally {
        setAstroStatsLoading(false);
      }
    };

    fetchAstroStats();
  }, [astroStatsYear]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  const getEarningSource = (t) => {
    if (!t.reference_type) return t.purpose || "N/A";
    const refType = t.reference_type.toLowerCase();
    if (refType === "booking") return "Puja";
    if (
      refType === "consultation_booking" ||
      ["chat", "call", "video"].includes(refType)
    )
      return "Consultation";
    if (refType === "report_booking") return "Report";
    if (refType === "question_booking") return "Question";
    return refType.charAt(0).toUpperCase() + refType.slice(1);
  };

  const {
    users = 0,
    astrologers = 0,
    bookings = 0,
    reportBookings = 0,
    pendingWithdrawals: pendingCount = 0,
  } = data;

  const overviewCounts = [
    { name: "Users", value: users },
    { name: "Astrologers", value: astrologers },
    { name: "Puja booking", value: bookings },
    { name: "Report Bookings", value: reportBookings },
  ].filter((item) => item.value > 0);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">Dashboard Overview</h2>
        <div className="nav nav-pills bg-light p-1 rounded">
          <button
            className={`nav-link border-0 ${activeTab === "overview" ? "active shadow-sm" : "text-muted"}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`nav-link border-0 ${activeTab === "earnings" ? "active shadow-sm" : "text-muted"}`}
            onClick={() => setActiveTab("earnings")}
          >
            Admin Earnings
          </button>
        </div>
      </div>

      {activeTab === "overview" ? (
        <>
          {/* KPI Cards */}
          <div className="row g-4 mb-5">
            {[
              {
                title: "Users",
                value: users,
                icon: "👥",
                color: "primary",
                path: "/admin/user-management/user/list/1",
              },
              {
                title: "Astrologers",
                value: astrologers,
                icon: "🔮",
                color: "info",
                path: "/admin/astrologer-management/astrologer/list/1",
              },
              {
                title: "Puja booking",
                value: bookings,
                icon: "📋",
                color: "success",
                path: "/admin/puja-booking/list/1",
              },
              {
                title: "Report Bookings",
                value: reportBookings,
                icon: "📄",
                color: "secondary",
                path: "/admin/report/booking-list/1",
              },
              {
                title: "Pending Transfers",
                value: pendingCount,
                icon: "⏳",
                color: "warning",
                path: "/admin/withdrawal/manual-list",
              },
              {
                title: "Admin Earnings",
                value: `₹${earnings.totalEarning || 0}`,
                icon: "💰",
                color: "danger",
                onClick: () => setActiveTab("earnings"),
              },
            ].map((kpi, i) => (
              <div key={i} className="col-md">
                <div
                  className={`card border-0 shadow-sm text-white bg-${kpi.color} h-100`}
                  role="button"
                  style={{ cursor: "pointer", transition: "transform 0.2s" }}
                  onClick={kpi.onClick || (() => navigate(kpi.path))}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-5px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <div className="card-body text-center py-4">
                    <div className="fs-1 mb-3">{kpi.icon}</div>
                    <h5 className="mb-2">{kpi.title}</h5>
                    <h3 className="mb-0">
                      {typeof kpi.value === "number"
                        ? kpi.value.toLocaleString()
                        : kpi.value}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4 mb-5">
            <div className="col-lg-12">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Overview by Count</h5>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center">
                  {overviewCounts.length === 0 ? (
                    <p className="text-muted">No data available</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={overviewCounts}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={40}
                          paddingAngle={3}
                          labelLine={true}
                          label={(props) => {
                            const {
                              cx,
                              cy,
                              midAngle,
                              outerRadius,
                              name,
                              value,
                            } = props;
                            const RADIAN = Math.PI / 180;
                            const radius = outerRadius + 30;
                            const x =
                              cx + radius * Math.cos(-midAngle * RADIAN);
                            const y =
                              cy + radius * Math.sin(-midAngle * RADIAN);

                            return (
                              <text
                                x={x}
                                y={y}
                                fill="#333"
                                textAnchor={x > cx ? "start" : "end"}
                                dominantBaseline="central"
                                fontSize="12"
                                fontWeight="500"
                              >
                                {`${name}`}
                                <tspan
                                  x={x}
                                  dy="1.2em"
                                  fontWeight="bold"
                                  fill="#198754"
                                >
                                  {value.toLocaleString()}
                                </tspan>
                              </text>
                            );
                          }}
                        >
                          {overviewCounts.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => Number(value).toLocaleString()}
                          contentStyle={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={50}
                          formatter={(value) => (
                            <span style={{ fontWeight: "500" }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <h5 className="mb-0">Users — Month Wise</h5>
                    <small className="text-muted">
                      Total in {userStatsYear}:{" "}
                      {yearlyTotalUsers.toLocaleString()}
                    </small>
                  </div>
                  <select
                    className="form-select w-auto"
                    value={userStatsYear}
                    onChange={(e) => setUserStatsYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="card-body">
                  {userStatsLoading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : userMonthlyStats.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      No user data available
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={380}>
                      <BarChart
                        data={userMonthlyStats}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis
                          allowDecimals={false}
                          label={{
                            value: "New Users",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip
                          formatter={(value) => [value, "Users"]}
                          labelFormatter={(label) => `Month: ${label}`}
                        />
                        <Legend />
                        <Bar
                          dataKey="users"
                          fill="#0d6efd"
                          name="New Users"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <h5 className="mb-0">Astrologers — Month Wise</h5>
                    <small className="text-muted">
                      Total in {astroStatsYear}:{" "}
                      {yearlyTotalAstrologers.toLocaleString()}
                      {highestAstroMonth ? (
                        <>
                          {" "}
                          · Peak: {highestAstroMonth} ({highestAstroMonthCount})
                        </>
                      ) : null}
                    </small>
                  </div>
                  <select
                    className="form-select w-auto"
                    value={astroStatsYear}
                    onChange={(e) => setAstroStatsYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="card-body">
                  {astroStatsLoading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border spinner-border-sm text-info"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : astroMonthlyStats.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      No astrologer data available
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={380}>
                      <BarChart
                        data={astroMonthlyStats}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis
                          allowDecimals={false}
                          label={{
                            value: "New Astrologers",
                            angle: -90,
                            position: "insideLeft",
                          }}
                        />
                        <Tooltip
                          formatter={(value) => [value, "Astrologers"]}
                          labelFormatter={(label) => `Month: ${label}`}
                        />
                        <Legend />
                        <Bar
                          dataKey="astrologers"
                          fill="#17a2b8"
                          name="New Astrologers"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="row g-4">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center d-flex flex-column justify-content-center align-items-center">
                <h5 className="text-muted mb-3">Admin Wallet Balance</h5>
                <h2 className="display-5 fw-bold text-success mb-0">
                  {earnings.currency === "USD" ? "$" : "₹"}
                  {earnings.balance?.toLocaleString() || 0}
                </h2>
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Earning Transactions</h5>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() =>
                    navigate("/admin/payment-management/wallet-history/1")
                  }
                >
                  View All
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Purpose</th>
                      <th>Astrologer</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.transactions?.length > 0 ? (
                      earnings.transactions.map((t) => (
                        <tr key={t.id}>
                          <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                          <td>{getEarningSource(t)}</td>
                          <td>{t.associated_astrologer?.name || "N/A"}</td>
                          <td className="fw-bold text-success">
                            + ₹{t.amount}
                            {t.admin_commission_percentage !== null && (
                              <span
                                className="text-muted fw-normal ms-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                ({t.admin_commission_percentage}%)
                              </span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge bg-${t.status === "success" ? "success" : "warning"}`}
                            >
                              {t.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No recent earnings
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
